import React, { useState, useMemo } from 'react';
import {
  Layers,
  BookOpen,
  Sparkles,
  Zap,
  Gauge,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Shuffle,
  Eye,
  Activity,
  Sliders,
  Compass,
  ArrowRight,
  Info,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';

// ============================================================================
// FLASHCARDS DATA
// ============================================================================
interface Flashcard {
  id: string;
  category: 'Electrical' | 'Mechanical' | 'USPS 955' | 'Safety & LOTO';
  front: string;
  back: string;
  formula?: string;
  keyRule: string;
}

const FLASHCARDS: Flashcard[] = [
  {
    id: 'fc-1',
    category: 'Electrical',
    front: 'Synchronous Motor Speed Formula',
    back: 'Ns = (120 × f) / p\n\nWhere f = supply frequency in Hz (60 Hz in US), and p = number of magnetic poles.\nExample: 4-pole motor at 60 Hz = (120 × 60) / 4 = 1,800 RPM.',
    formula: 'Ns = (120 × f) / p',
    keyRule: 'Rotor speed under load is always slightly lower due to slip (typically 2-5% slip).',
  },
  {
    id: 'fc-2',
    category: 'Electrical',
    front: '3-Phase AC Real Power (Watts)',
    back: 'P = √3 × V_LL × I_L × PF\n\nWhere √3 ≈ 1.732, V_LL is line-to-line voltage (e.g. 480V), I_L is line current in amps, and PF is power factor (0.0 to 1.0).',
    formula: 'P = √3 × V_LL × I_L × PF',
    keyRule: 'Always use line-to-line voltage (not phase-to-neutral) with the √3 factor.',
  },
  {
    id: 'fc-3',
    category: 'Electrical',
    front: 'High-Leg Delta (Wild-Leg) Voltage to Neutral',
    back: 'In a 240/120V 3-phase 4-wire Delta system, Phase B (the high leg) has a nominal voltage to neutral of 120V × √3 ≈ 208V AC.\n\nNever connect 120V loads to the high leg!',
    formula: 'V_high_leg = 120V × √3 ≈ 208V',
    keyRule: 'NEC requires high-leg conductor to be identified with ORANGE marking.',
  },
  {
    id: 'fc-4',
    category: 'Electrical',
    front: 'VFD DC Bus Voltage Calculation',
    back: 'V_DC ≈ V_AC_in × √2 (≈ 1.414)\n\nFor a 480V AC drive: 480 × 1.414 ≈ 678V DC at idle or light load.\nFor a 230V AC drive: 230 × 1.414 ≈ 325V DC.',
    formula: 'V_DC = V_AC × 1.414',
    keyRule: 'A significantly lower DC bus reading indicates an open diode in the input bridge rectifier.',
  },
  {
    id: 'fc-5',
    category: 'Electrical',
    front: 'Sinking vs Sourcing DC Inputs (PNP vs NPN)',
    back: 'Sinking Module (NPN interface) = inputs connected to 0V/COM internally; requires a Sourcing (PNP) sensor switching +24VDC.\n\nSourcing Module (PNP interface) = inputs connected to +24V internally; requires a Sinking (NPN) sensor switching 0V/GND.',
    keyRule: 'Rule of thumb: Sensor type and Module type must be OPPOSITE (Sinking input takes Sourcing PNP sensor).',
  },
  {
    id: 'fc-6',
    category: 'Mechanical',
    front: 'Hydraulic Cylinder Force (Extend)',
    back: 'Force (lbs) = Pressure (PSI) × Area (sq in)\n\nArea of piston = π × r² = (π × d²) / 4\nExample: 4-inch bore cylinder at 1,500 PSI:\nArea = 3.1416 × 2² = 12.57 sq in\nForce = 1,500 × 12.57 = 18,850 lbs.',
    formula: 'F = P × A = P × (π × d² / 4)',
    keyRule: 'On retraction, subtract the rod area from the piston area (Annular Area).',
  },
  {
    id: 'fc-7',
    category: 'Mechanical',
    front: 'Shaft Torque from Horsepower & RPM',
    back: 'Torque (ft-lbs) = (5,252 × HP) / RPM\n\nExample: 10 HP motor running at 1,750 RPM:\nTorque = (5,252 × 10) / 1,750 = 30.0 ft-lbs.',
    formula: 'T = (5,252 × HP) / RPM',
    keyRule: 'Horsepower and Torque are always equal at exactly 5,252 RPM.',
  },
  {
    id: 'fc-8',
    category: 'Mechanical',
    front: 'Centrifugal Pump Affinity Laws (Speed Change)',
    back: '1. Flow: Q₂/Q₁ = N₂/N₁ (direct)\n2. Head: H₂/H₁ = (N₂/N₁)² (square)\n3. Power: P₂/P₁ = (N₂/N₁)³ (cube)\n\nExample: Doubling pump speed quadruples head (4X) and increases required motor horsepower by 8X!',
    formula: 'Q ∝ N,  H ∝ N²,  P ∝ N³',
    keyRule: 'Be cautious when speeding up a pump; motor amp draw increases with the cube of speed.',
  },
  {
    id: 'fc-9',
    category: 'Mechanical',
    front: 'Standard V-Belt Tension Rule of Thumb',
    back: 'Deflection = 1/64 inch per 1 inch of span length.\n\nExample: A 32-inch belt span should deflect exactly 32/64" = 0.5 inches under recommended spring gauge force.',
    formula: 'Deflection = Span (in) / 64',
    keyRule: 'Measure deflection at the center of the belt span perpendicular to the belt.',
  },
  {
    id: 'fc-10',
    category: 'Mechanical',
    front: 'ANSI Roller Chain Pitch Numbering',
    back: 'First digit(s) indicate pitch in eighths of an inch (1/8").\n• #40 Chain = 4/8" = 1/2" pitch\n• #50 Chain = 5/8" pitch\n• #60 Chain = 6/8" = 3/4" pitch\n• #80 Chain = 8/8" = 1.0" pitch',
    keyRule: 'Replace roller chain when pitch elongation exceeds 3% to prevent sprocket tooth damage.',
  },
  {
    id: 'fc-11',
    category: 'USPS 955',
    front: 'Rigging Sling Angle Stress Multiplier',
    back: 'Tension per Leg = (Total Load / Number of Legs) / sin(θ)\n\n• 60° horizontal angle: Tension = Load / 1.732 (approx 1.15X factor)\n• 45° angle: Tension = Load / 1.414 (approx 1.41X factor)\n• 30° angle: Tension per leg = 100% of entire load weight (2.0X factor)!',
    formula: 'T = (W / n) / sin(θ)',
    keyRule: 'Never rig with horizontal sling angles below 30 degrees.',
  },
  {
    id: 'fc-12',
    category: 'USPS 955',
    front: 'Refrigeration Superheat & Subcooling (BEM)',
    back: 'Superheat = Suction Line Temp - Evaporator Saturation Temp (protects compressor from liquid floodback; normal 8-12°F).\n\nSubcooling = Condenser Saturation Temp - Liquid Line Temp (ensures 100% liquid enters TXV; normal 10-15°F).',
    keyRule: 'High superheat + high subcooling indicates a liquid line restriction (plugged drier or stuck TXV).',
  },
  {
    id: 'fc-13',
    category: 'USPS 955',
    front: 'De Morgan’s Laws of Digital Logic (ET)',
    back: '1. NOT(A AND B) = (NOT A) OR (NOT B)\n2. NOT(A OR B) = (NOT A) AND (NOT B)\n\nA NAND gate is logically equivalent to an OR gate with active-low inverted inputs.',
    formula: '!(A · B) = !A + !B,   !(A + B) = !A · !B',
    keyRule: 'Break the bar and change the sign.',
  },
  {
    id: 'fc-14',
    category: 'Safety & LOTO',
    front: 'OSHA 1910.147 Zero Energy State Verification',
    back: 'After applying locks and tags and dissipating stored energy (air, hydraulic pressure, gravity blocks):\n\n1. Attempt to restart machine using local start controls (pushbuttons, switches).\n2. Test electrical conductors with an approved test meter.\n3. Return controls to OFF before commencing work.',
    keyRule: 'Lockout is NEVER complete without physical verification of zero energy state.',
  },
  {
    id: 'fc-15',
    category: 'Safety & LOTO',
    front: 'Multimeter Overvoltage Category Ratings (IEC 61010)',
    back: '• CAT I: Electronic equipment, signal level.\n• CAT II: Receptacle-connected appliances, plug-in loads.\n• CAT III: 480V 3-phase distribution, motor control centers (MCCs), feeders, bus ducts.\n• CAT IV: Origin of installation, utility service drop, electricity meters.',
    keyRule: 'Always use minimum CAT III 1000V or CAT IV 600V rated meters in industrial electrical panels.',
  },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export interface StudyLabPageProps {
  onNavigate: (path: string) => void;
  onStartExam: (track: any, mode: any) => void;
}

export function StudyLabPage({ onNavigate, onStartExam }: StudyLabPageProps) {
  const [activeTab, setActiveTab] = useState<'spatial' | 'schematics' | 'flashcards' | 'formulas'>('spatial');

  // Flashcards state
  const [selectedCardCat, setSelectedCardCat] = useState<string>('All');
  const [cardIndex, setCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [masteredCards, setMasteredCards] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('mastered_flashcards');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const filteredCards = useMemo(() => {
    if (selectedCardCat === 'All') return FLASHCARDS;
    return FLASHCARDS.filter((c) => c.category === selectedCardCat);
  }, [selectedCardCat]);

  const currentCard = filteredCards[cardIndex] || filteredCards[0];

  const handleNextCard = () => {
    setIsFlipped(false);
    setCardIndex((prev) => (prev + 1) % filteredCards.length);
  };

  const handlePrevCard = () => {
    setIsFlipped(false);
    setCardIndex((prev) => (prev - 1 + filteredCards.length) % filteredCards.length);
  };

  const toggleMastered = (id: string) => {
    setMasteredCards((prev) => {
      const next = prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
      localStorage.setItem('mastered_flashcards', JSON.stringify(next));
      return next;
    });
  };

  // --------------------------------------------------------------------------
  // INTERACTIVE SCHEMATICS SIMULATOR STATE
  // --------------------------------------------------------------------------
  // Motor Starter State
  const [isStartPressed, setIsStartPressed] = useState(false);
  const [isStopPressed, setIsStopPressed] = useState(false);
  const [isOverloadTripped, setIsOverloadTripped] = useState(false);
  const [isMotorRunning, setIsMotorRunning] = useState(false);

  const handlePressStart = () => {
    if (isOverloadTripped || isStopPressed) return;
    setIsStartPressed(true);
    setIsMotorRunning(true);
    setTimeout(() => setIsStartPressed(false), 300); // momentary release
  };

  const handlePressStop = () => {
    setIsStopPressed(true);
    setIsMotorRunning(false);
    setTimeout(() => setIsStopPressed(false), 400);
  };

  const handleToggleOverload = () => {
    if (!isOverloadTripped) {
      setIsOverloadTripped(true);
      setIsMotorRunning(false);
    } else {
      setIsOverloadTripped(false);
    }
  };

  // Hydraulic Valve Simulator State
  const [spoolPosition, setSpoolPosition] = useState<'neutral' | 'extend' | 'retract'>('neutral');

  // 4-20mA Transmitter State
  const [sensorPressure, setSensorPressure] = useState<number>(50); // 0-100 PSI
  const calculatedCurrent = (4 + (sensorPressure / 100) * 16).toFixed(2);
  const calculatedCounts = Math.round((sensorPressure / 100) * 32767);

  // --------------------------------------------------------------------------
  // SPATIAL LAB STATE (Cube Folding & Gears)
  // --------------------------------------------------------------------------
  const [activeCubeQuestion, setActiveCubeQuestion] = useState(0);
  const [cubeSelectedAnswer, setCubeSelectedAnswer] = useState<number | null>(null);
  const [gearCount, setGearCount] = useState<number>(4);

  const CUBE_QUESTIONS = [
    {
      question: 'When the 2D cross pattern below is folded into a 3D closed cube, which face will be directly OPPOSITE the face with the solid circle (●)?',
      netLabels: ['TOP', '●', '▲', '■', '★', '♦'],
      correctIndex: 2, // '▲'
      options: ['TOP (Square 1)', '■ (Square 4)', '▲ (Square 3 - separated by 1 intervening square)', '★ (Square 5)'],
      explanation: 'In a flat net of a cube, faces that are opposite each other in the folded 3D cube are separated by exactly ONE intervening square along a straight row or column.',
    },
    {
      question: 'Can two squares that share a common folding edge in a 2D net ever be on opposite faces of the folded cube?',
      netLabels: ['1', '2', '3', '4', '5', '6'],
      correctIndex: 1, // 'No'
      options: [
        'Yes, if the cube is folded counter-clockwise',
        'No, adjacent squares sharing a fold line will always meet at a 90-degree angle (adjacent faces)',
        'Only on odd-numbered dice',
        'Yes, in third-angle projection',
      ],
      explanation: 'Adjacent squares sharing a fold line fold at 90 degrees to each other in 3D space, making it geometrically impossible for them to be opposite faces.',
    },
  ];

  return (
    <div className="container max-w-6xl py-8 space-y-8 pb-16">
      {/* Header Banner */}
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-card to-amber-500/5 p-6 md:p-8 backdrop-blur-md shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5" />
              USPS 955 &amp; Ramsay Technical Study Lab
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight">
              Interactive Schematics, Spatial Relations &amp; Formulas
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
              Explore interactive electrical ladder logic, hydraulic flow simulators, USPS 955 3D spatial reasoning drills, and high-yield formula cheat sheets.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="amber"
              size="md"
              onClick={() => onStartExam('all', 'timed_mock')}
              className="gap-2 font-bold shadow-lg shadow-amber-500/20"
            >
              <Zap className="h-4 w-4" />
              Launch Mock Exam
            </Button>
          </div>
        </div>

        {/* Featured Electrical Schematics Course Banner */}
        <div className="mt-6 rounded-xl border border-sky-500/30 bg-gradient-to-r from-sky-50 via-card to-amber-50/50 dark:from-sky-950/60 dark:via-slate-900 dark:to-amber-950/30 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="h-10 w-10 rounded-lg bg-sky-500/20 border border-sky-500/40 flex items-center justify-center shrink-0">
              <BookOpen className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-700 dark:text-sky-300">
                  NEW COURSE
                </span>
                <span className="text-xs font-bold text-foreground">Electrical Schematics &amp; Blueprint Reading</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                24 illustrated lessons, 5 interactive diagnostic benches, symbol dictionary, and 30-question final certification exam.
              </p>
            </div>
          </div>
          <Button
            variant="amber"
            size="sm"
            onClick={() => onNavigate('/course/schematics')}
            className="text-xs font-bold gap-1.5 shrink-0 self-start sm:self-auto"
          >
            Start Course
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap items-center gap-2 mt-6 pt-6 border-t border-border/60">
          <button
            onClick={() => setActiveTab('spatial')}
            className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold flex items-center gap-2 transition-all ${
              activeTab === 'spatial'
                ? 'bg-purple-500 text-white shadow-md shadow-purple-500/20'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <Compass className="h-4 w-4" />
            Spatial Relations Lab (USPS 955)
          </button>

          <button
            onClick={() => setActiveTab('schematics')}
            className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold flex items-center gap-2 transition-all ${
              activeTab === 'schematics'
                ? 'bg-sky-500 text-white shadow-md shadow-sky-500/20'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <Activity className="h-4 w-4" />
            Interactive Schematics Simulator
          </button>

          <button
            onClick={() => setActiveTab('flashcards')}
            className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold flex items-center gap-2 transition-all ${
              activeTab === 'flashcards'
                ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <Layers className="h-4 w-4" />
            Interactive Flashcards ({masteredCards.length}/{FLASHCARDS.length} Mastered)
          </button>

          <button
            onClick={() => setActiveTab('formulas')}
            className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold flex items-center gap-2 transition-all ${
              activeTab === 'formulas'
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            Formula Reference &amp; Cheat Sheet
          </button>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* 1. SPATIAL RELATIONS LAB TAB (USPS 955 Section 2) */}
      {/* ==================================================================== */}
      {activeTab === 'spatial' && (
        <div className="space-y-8">
          {/* Overview Callout */}
          <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-4 text-xs md:text-sm text-purple-900 dark:text-purple-200 flex items-start gap-3">
            <Info className="h-5 w-5 shrink-0 text-purple-500 mt-0.5" />
            <div>
              <strong className="font-bold">USPS Exam 955 Spatial Relations Section:</strong> In the real Postal Exam 955, you will encounter 65–68 spatial visualization questions in 40 minutes. Key concepts include folding 2D nets into 3D objects, matching orthographic perspectives (Top, Front, Right), and predicting gear rotation directions.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Interactive 3D Cube Net Simulator */}
            <Card className="border-purple-500/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">USPS 955 Spatial Drill</Badge>
                  <span className="text-xs text-muted-foreground">Question {activeCubeQuestion + 1} of {CUBE_QUESTIONS.length}</span>
                </div>
                <CardTitle className="text-lg">3D Cube Net Folding Challenge</CardTitle>
                <CardDescription>
                  Mentally fold the flat 2D pattern into a closed 3D cube.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm font-medium text-foreground">
                  {CUBE_QUESTIONS[activeCubeQuestion].question}
                </p>

                {/* SVG 2D Cube Net Rendering */}
                <div className="bg-slate-100 dark:bg-slate-900/90 rounded-xl p-6 border border-border flex items-center justify-center">
                  <svg width="220" height="220" viewBox="0 0 200 200" className="drop-shadow-md">
                    {/* Square 1: Top */}
                    <rect x="70" y="10" width="45" height="45" fill="#f8fafc" stroke="#64748b" strokeWidth="2" rx="4" />
                    <text x="92" y="38" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#0f172a">TOP</text>

                    {/* Square 2: Left arm */}
                    <rect x="25" y="55" width="45" height="45" fill="#e2e8f0" stroke="#64748b" strokeWidth="2" rx="4" />
                    <circle cx="47" cy="77" r="10" fill="#dc2626" />

                    {/* Square 3: Center */}
                    <rect x="70" y="55" width="45" height="45" fill="#f8fafc" stroke="#64748b" strokeWidth="2" rx="4" />
                    <polygon points="92,67 82,87 102,87" fill="#2563eb" />

                    {/* Square 4: Right arm */}
                    <rect x="115" y="55" width="45" height="45" fill="#e2e8f0" stroke="#64748b" strokeWidth="2" rx="4" />
                    <rect x="127" y="67" width="20" height="20" fill="#16a34a" />

                    {/* Square 5: Below Center */}
                    <rect x="70" y="100" width="45" height="45" fill="#f8fafc" stroke="#64748b" strokeWidth="2" rx="4" />
                    <text x="92" y="130" textAnchor="middle" fontSize="18" fill="#d97706">★</text>

                    {/* Square 6: Bottom */}
                    <rect x="70" y="145" width="45" height="45" fill="#f8fafc" stroke="#64748b" strokeWidth="2" rx="4" />
                    <polygon points="92,155 102,167 92,179 82,167" fill="#9333ea" />
                  </svg>
                </div>

                {/* Multiple Choice Options */}
                <div className="space-y-2">
                  {CUBE_QUESTIONS[activeCubeQuestion].options.map((opt, idx) => {
                    const isSelected = cubeSelectedAnswer === idx;
                    const isCorrect = idx === CUBE_QUESTIONS[activeCubeQuestion].correctIndex;
                    return (
                      <button
                        key={idx}
                        onClick={() => setCubeSelectedAnswer(idx)}
                        className={`w-full text-left p-3 rounded-lg border text-xs md:text-sm font-medium transition-all ${
                          cubeSelectedAnswer !== null
                            ? isCorrect
                              ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-800 dark:text-emerald-300 font-bold'
                              : isSelected
                              ? 'bg-rose-500/15 border-rose-500/50 text-rose-800 dark:text-rose-300'
                              : 'bg-card border-border text-muted-foreground opacity-60'
                            : isSelected
                            ? 'bg-purple-500/15 border-purple-500 text-purple-900 dark:text-purple-200'
                            : 'bg-card border-border hover:bg-muted text-foreground'
                        }`}
                      >
                        <span className="inline-block w-6 font-bold">{String.fromCharCode(65 + idx)}.</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>

                {/* Feedback & Explanation */}
                {cubeSelectedAnswer !== null && (
                  <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800/80 text-xs text-muted-foreground border border-border">
                    <strong className="text-foreground">Explanation: </strong>
                    {CUBE_QUESTIONS[activeCubeQuestion].explanation}
                  </div>
                )}

                <div className="flex justify-between items-center pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={activeCubeQuestion === 0}
                    onClick={() => {
                      setActiveCubeQuestion((prev) => prev - 1);
                      setCubeSelectedAnswer(null);
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={activeCubeQuestion === CUBE_QUESTIONS.length - 1}
                    onClick={() => {
                      setActiveCubeQuestion((prev) => prev + 1);
                      setCubeSelectedAnswer(null);
                    }}
                  >
                    Next Question
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Interactive Gear Rotation Train Simulator */}
            <Card className="border-purple-500/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="warning">Mechanical Reasoning</Badge>
                  <span className="text-xs font-mono text-muted-foreground">{gearCount} Gears in Mesh</span>
                </div>
                <CardTitle className="text-lg">Gear Train Direction Simulator</CardTitle>
                <CardDescription>
                  External gears reverse direction at every mesh. Test how an odd vs even number of gears affects output rotation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Gear count slider */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-medium text-muted-foreground">
                    <span>Number of Meshing Gears:</span>
                    <span className="font-bold text-foreground">{gearCount} Gears</span>
                  </div>
                  <input
                    type="range"
                    min="2"
                    max="6"
                    value={gearCount}
                    onChange={(e) => setGearCount(parseInt(e.target.value))}
                    className="w-full accent-purple-500"
                  />
                </div>

                {/* SVG Visual Gear Train */}
                <div className="bg-slate-100 dark:bg-slate-900/90 rounded-xl p-4 border border-border overflow-x-auto flex items-center justify-center min-h-[160px]">
                  <svg width={gearCount * 65 + 30} height="130" viewBox={`0 0 ${gearCount * 65 + 30} 130`}>
                    {Array.from({ length: gearCount }).map((_, i) => {
                      const cx = 40 + i * 58;
                      const cy = 65;
                      const isClockwise = i % 2 === 0;
                      return (
                        <g key={i}>
                          {/* Gear Circle */}
                          <circle
                            cx={cx}
                            cy={cy}
                            r="26"
                            fill={i === 0 ? '#dbeafe' : i === gearCount - 1 ? '#fef3c7' : '#f1f5f9'}
                            stroke={i === 0 ? '#2563eb' : i === gearCount - 1 ? '#d97706' : '#64748b'}
                            strokeWidth="3"
                            strokeDasharray="6 3"
                          />
                          <circle cx={cx} cy={cy} r="6" fill="#475569" />
                          <text x={cx} y={cy + 3} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#0f172a">
                            G{i + 1}
                          </text>

                          {/* Direction Arrow */}
                          <text x={cx} y={cy - 30} textAnchor="middle" fontSize="14" fill={isClockwise ? '#2563eb' : '#dc2626'}>
                            {isClockwise ? '↻ CW' : '↺ CCW'}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* Result Callout */}
                <div className="p-4 rounded-xl border border-border bg-card space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-muted-foreground">Driver Gear (G1):</span>
                    <span className="font-bold text-blue-600 dark:text-blue-400">CLOCKWISE (CW)</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-muted-foreground">Final Output Gear (G{gearCount}):</span>
                    <span className={`font-bold ${gearCount % 2 === 1 ? 'text-blue-600 dark:text-blue-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {gearCount % 2 === 1 ? 'CLOCKWISE (Same as Driver)' : 'COUNTER-CLOCKWISE (Opposite Driver)'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                    💡 <strong>Exam Rule:</strong> An <strong>odd</strong> number of external spur gears (3, 5, 7) results in the output gear rotating in the <em>same</em> direction as the driver. An <strong>even</strong> number (2, 4, 6) <em>inverts</em> the direction.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 2. INTERACTIVE SCHEMATICS SIMULATOR TAB */}
      {/* ==================================================================== */}
      {activeTab === 'schematics' && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Interactive 3-Wire Motor Starter Ladder Circuit */}
            <Card className="border-sky-500/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">Electrical Troubleshooting</Badge>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isMotorRunning ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>
                    {isMotorRunning ? 'MOTOR RUNNING' : 'MOTOR STOPPED'}
                  </span>
                </div>
                <CardTitle className="text-lg">3-Wire Start/Stop Ladder Circuit Simulator</CardTitle>
                <CardDescription>
                  Click the pushbuttons and overload contact to observe live holding-circuit (seal-in) logic.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Interactive Controls */}
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="amber"
                    size="sm"
                    onMouseDown={handlePressStart}
                    className="font-bold gap-1.5"
                  >
                    <Play className="h-3.5 w-3.5 fill-current" />
                    Press START (PB2)
                  </Button>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handlePressStop}
                    className="font-bold gap-1.5"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Press STOP (PB1)
                  </Button>

                  <Button
                    variant={isOverloadTripped ? 'destructive' : 'secondary'}
                    size="sm"
                    onClick={handleToggleOverload}
                    className="font-bold text-xs"
                  >
                    {isOverloadTripped ? 'Reset Overload (OL)' : 'Trip Overload (OL)'}
                  </Button>
                </div>

                {/* SVG Ladder Diagram */}
                <div className="bg-slate-100 dark:bg-slate-900/90 rounded-xl p-4 border border-border">
                  <svg width="100%" height="160" viewBox="0 0 460 160" className="w-full">
                    {/* Power Rails */}
                    <line x1="20" y1="20" x2="20" y2="140" stroke="#0284c7" strokeWidth="4" />
                    <text x="15" y="15" fontSize="11" fontWeight="bold" fill="#0284c7">L1 (120V)</text>

                    <line x1="440" y1="20" x2="440" y2="140" stroke="#64748b" strokeWidth="4" />
                    <text x="425" y="15" fontSize="11" fontWeight="bold" fill="#64748b">L2 (Neutral)</text>

                    {/* STOP Button (NC) */}
                    <g transform="translate(60, 40)">
                      <line x1="-40" y1="20" x2="0" y2="20" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <circle cx="0" cy="20" r="4" fill="#0f172a" />
                      <line x1="0" y1="12" x2="35" y2="12" stroke={isStopPressed ? '#dc2626' : '#0f172a'} strokeWidth="3" />
                      <circle cx="35" cy="20" r="4" fill="#0f172a" />
                      <line x1="17" y1="12" x2="17" y2="0" stroke="#0f172a" strokeWidth="2" />
                      <rect x="7" y="-3" width="20" height="4" fill="#dc2626" />
                      <text x="17" y="-6" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#dc2626">STOP (NC)</text>
                    </g>

                    {/* START Button (NO) - Upper Rung */}
                    <g transform="translate(140, 40)">
                      <line x1="-45" y1="20" x2="0" y2="20" stroke={isMotorRunning || isStartPressed ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <circle cx="0" cy="20" r="4" fill="#0f172a" />
                      <line x1="0" y1={isStartPressed ? '20' : '10'} x2="40" y2={isStartPressed ? '20' : '10'} stroke={isStartPressed ? '#059669' : '#0f172a'} strokeWidth="3" />
                      <circle cx="40" cy="20" r="4" fill="#0f172a" />
                      <line x1="20" y1={isStartPressed ? '20' : '10'} x2="20" y2="-2" stroke="#0f172a" strokeWidth="2" />
                      <rect x="10" y="-5" width="20" height="4" fill="#16a34a" />
                      <text x="20" y="-8" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#16a34a">START (NO)</text>
                    </g>

                    {/* Holding Contact M (NO) - Lower Rung */}
                    <g transform="translate(140, 95)">
                      <line x1="-15" y1="-35" x2="-15" y2="0" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <line x1="-15" y1="0" x2="0" y2="0" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <line x1="0" y1="-8" x2="0" y2="8" stroke="#0f172a" strokeWidth="3" />
                      <line x1="12" y1="-8" x2="12" y2="8" stroke="#0f172a" strokeWidth="3" />
                      <line x1="12" y1="0" x2="55" y2="0" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <line x1="55" y1="0" x2="55" y2="-35" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <text x="6" y="-12" textAnchor="middle" fontSize="10" fontWeight="bold" fill={isMotorRunning ? '#059669' : '#64748b'}>
                        Aux Contact M (Seal-In)
                      </text>
                    </g>

                    {/* Contactor Coil M */}
                    <g transform="translate(260, 60)">
                      <line x1="-80" y1="0" x2="0" y2="0" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <circle cx="20" cy="0" r="18" fill={isMotorRunning ? '#dcfce7' : '#f8fafc'} stroke={isMotorRunning ? '#16a34a' : '#475569'} strokeWidth="3" />
                      <text x="20" y="4" textAnchor="middle" fontSize="12" fontWeight="bold" fill={isMotorRunning ? '#15803d' : '#0f172a'}>M</text>
                      <text x="20" y="28" textAnchor="middle" fontSize="10" fill="#64748b">Coil</text>
                    </g>

                    {/* Overload Contact OL (NC) */}
                    <g transform="translate(340, 60)">
                      <line x1="-42" y1="0" x2="0" y2="0" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <path d="M 0 0 L 10 -6 L 20 6 L 30 0" fill="none" stroke={isOverloadTripped ? '#dc2626' : '#0f172a'} strokeWidth="3" />
                      <line x1="30" y1="0" x2="100" y2="0" stroke={isMotorRunning ? '#059669' : '#94a3b8'} strokeWidth="3" />
                      <text x="15" y="-10" textAnchor="middle" fontSize="10" fontWeight="bold" fill={isOverloadTripped ? '#dc2626' : '#64748b'}>
                        {isOverloadTripped ? 'OL TRIPPED (OPEN)' : 'OL (NC)'}
                      </text>
                    </g>
                  </svg>
                </div>

                <div className="text-xs text-muted-foreground p-3 rounded-xl bg-card border border-border space-y-1">
                  <p><strong>Circuit Explanation:</strong> When momentary <strong>START</strong> is pressed, coil <strong>M</strong> energizes, pulling in auxiliary contact <strong>M</strong> in parallel with START. When you release START, current continues flowing through contact M (holding circuit / seal-in).</p>
                  <p className="text-amber-600 dark:text-amber-400">💡 Pressing <strong>STOP</strong> or tripping the thermal <strong>OL</strong> breaks continuity, dropping out coil M.</p>
                </div>
              </CardContent>
            </Card>

            {/* 4-20mA Analog Current Loop Simulator */}
            <Card className="border-sky-500/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="warning">Instrumentation &amp; PLCs</Badge>
                  <span className="text-xs font-mono font-bold text-sky-600 dark:text-sky-400">{calculatedCurrent} mA</span>
                </div>
                <CardTitle className="text-lg">4–20 mA Analog Loop Transmitter Simulator</CardTitle>
                <CardDescription>
                  Adjust process pressure to observe current loop scaling and 16-bit PLC input counts.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-medium text-muted-foreground">
                    <span>Process Pressure Input:</span>
                    <span className="font-bold text-foreground text-sm">{sensorPressure} PSI (0 - 100 PSI Span)</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={sensorPressure}
                    onChange={(e) => setSensorPressure(parseInt(e.target.value))}
                    className="w-full accent-sky-500"
                  />
                </div>

                {/* Readout Panels */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border border-sky-500/30 bg-sky-500/10 text-center space-y-1">
                    <span className="text-xs font-semibold text-muted-foreground">Loop Current (mA)</span>
                    <div className="text-2xl font-black text-sky-700 dark:text-sky-300 font-mono">
                      {calculatedCurrent} mA
                    </div>
                    <span className="text-[10px] text-muted-foreground">Span: 4 mA (0 PSI) to 20 mA (100 PSI)</span>
                  </div>

                  <div className="p-4 rounded-xl border border-border bg-card text-center space-y-1">
                    <span className="text-xs font-semibold text-muted-foreground">PLC 16-Bit Raw Counts</span>
                    <div className="text-2xl font-black text-foreground font-mono">
                      {calculatedCounts}
                    </div>
                    <span className="text-[10px] text-muted-foreground">Range: 0 to 32,767 counts</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-border bg-card space-y-2 text-xs">
                  <div className="font-bold text-foreground flex items-center gap-1.5">
                    <Info className="h-4 w-4 text-sky-500" />
                    Formula &amp; Exam Takeaway:
                  </div>
                  <div className="font-mono bg-muted p-2 rounded text-[11px]">
                    Current (mA) = 4 mA + ((Measured PSI / 100 PSI) × 16 mA)
                  </div>
                  <p className="text-muted-foreground">
                    Why 4 mA instead of 0 mA? 4 mA is a <strong>"live zero"</strong>: if the wire breaks, current drops to 0.00 mA, alerting the PLC to an open circuit fault instead of mistaking it for zero pressure.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 3. FLASHCARDS DRILL TAB */}
      {/* ==================================================================== */}
      {activeTab === 'flashcards' && (
        <div className="space-y-6 max-w-2xl mx-auto">
          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {['All', 'Electrical', 'Mechanical', 'USPS 955', 'Safety & LOTO'].map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedCardCat(cat);
                  setCardIndex(0);
                  setIsFlipped(false);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  selectedCardCat === cat
                    ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20'
                    : 'bg-card border border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Flashcard Component */}
          <div
            onClick={() => setIsFlipped(!isFlipped)}
            className="cursor-pointer min-h-[280px] p-8 rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-card to-amber-500/5 shadow-xl shadow-amber-500/5 flex flex-col justify-between select-none transition-all hover:border-amber-500/60"
          >
            <div className="flex items-center justify-between">
              <Badge variant="warning">{currentCard.category}</Badge>
              <div className="flex items-center gap-2">
                {masteredCards.includes(currentCard.id) && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> Mastered
                  </span>
                )}
                <span className="text-xs font-mono text-muted-foreground">
                  {cardIndex + 1} / {filteredCards.length}
                </span>
              </div>
            </div>

            <div className="py-6 text-center space-y-4">
              {!isFlipped ? (
                <>
                  <span className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest">
                    QUESTION / CONCEPT
                  </span>
                  <h3 className="text-xl md:text-2xl font-extrabold text-foreground">
                    {currentCard.front}
                  </h3>
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Eye className="h-3.5 w-3.5" /> Click anywhere to reveal answer &amp; key rule
                  </p>
                </>
              ) : (
                <>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">
                    ANSWER &amp; TECHNICAL EXPLANATION
                  </span>
                  <div className="text-sm md:text-base text-foreground font-medium whitespace-pre-line leading-relaxed">
                    {currentCard.back}
                  </div>
                  {currentCard.formula && (
                    <div className="inline-block px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-800 dark:text-amber-300 font-mono text-sm font-bold">
                      {currentCard.formula}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground font-semibold pt-2">
                    ⚡ <strong>Key Rule:</strong> {currentCard.keyRule}
                  </p>
                </>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border/60 text-xs text-muted-foreground">
              <span>Card {cardIndex + 1} of {filteredCards.length}</span>
              <span>Click to {isFlipped ? 'Show Front' : 'Show Back'}</span>
            </div>
          </div>

          {/* Navigation & Action Controls */}
          <div className="flex items-center justify-between gap-4">
            <Button variant="secondary" size="md" onClick={handlePrevCard} className="gap-1.5">
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>

            <Button
              variant={masteredCards.includes(currentCard.id) ? 'outline' : 'secondary'}
              size="md"
              onClick={(e) => {
                e.stopPropagation();
                toggleMastered(currentCard.id);
              }}
              className="gap-1.5 text-xs font-bold"
            >
              <CheckCircle2 className={`h-4 w-4 ${masteredCards.includes(currentCard.id) ? 'text-emerald-500' : ''}`} />
              {masteredCards.includes(currentCard.id) ? 'Mark as Unmastered' : 'Mark as Mastered'}
            </Button>

            <Button variant="secondary" size="md" onClick={handleNextCard} className="gap-1.5">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 4. FORMULA CHEAT SHEET TAB */}
      {/* ==================================================================== */}
      {activeTab === 'formulas' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Electrical Formulas */}
            <Card className="border-sky-500/30">
              <CardHeader className="bg-sky-500/5 border-b border-border/60 pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-sky-600 dark:text-sky-400">
                  <Zap className="h-4 w-4" />
                  Electrical Formulas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4 text-xs">
                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">3-Phase Real Power (kW)</div>
                  <div className="font-mono text-sky-600 dark:text-sky-400 font-bold">P = √3 × V_LL × I_L × PF</div>
                  <p className="text-[11px] text-muted-foreground">√3 ≈ 1.732. Always use Line-to-Line voltage.</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Motor Synchronous Speed &amp; Slip</div>
                  <div className="font-mono text-sky-600 dark:text-sky-400 font-bold">Ns = (120 × f) / p</div>
                  <div className="font-mono text-slate-600 dark:text-slate-400 text-[11px]">Slip % = ((Ns - Nr) / Ns) × 100%</div>
                  <p className="text-[11px] text-muted-foreground">4-pole @ 60Hz = 1,800 RPM. 6-pole = 1,200 RPM.</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">VFD DC Bus Voltage</div>
                  <div className="font-mono text-sky-600 dark:text-sky-400 font-bold">V_DC ≈ V_AC × 1.414 (√2)</div>
                  <p className="text-[11px] text-muted-foreground">480V AC in ≈ 678V DC bus at idle.</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Resistors in Parallel</div>
                  <div className="font-mono text-sky-600 dark:text-sky-400 font-bold">1/R_total = 1/R₁ + 1/R₂ + 1/R₃</div>
                  <p className="text-[11px] text-muted-foreground">Two resistors: R_tot = (R₁ × R₂) / (R₁ + R₂).</p>
                </div>
              </CardContent>
            </Card>

            {/* Mechanical Formulas */}
            <Card className="border-amber-500/30">
              <CardHeader className="bg-amber-500/5 border-b border-border/60 pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <Gauge className="h-4 w-4" />
                  Mechanical &amp; Fluid Power
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4 text-xs">
                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Hydraulic Cylinder Force</div>
                  <div className="font-mono text-amber-600 dark:text-amber-400 font-bold">Force (lbs) = Pressure (PSI) × Area</div>
                  <div className="font-mono text-slate-600 dark:text-slate-400 text-[11px]">Area = π × r² = 0.7854 × d²</div>
                  <p className="text-[11px] text-muted-foreground">Retract Force uses Annular Area (A_bore - A_rod).</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Shaft Torque from HP</div>
                  <div className="font-mono text-amber-600 dark:text-amber-400 font-bold">Torque (ft-lbs) = (5,252 × HP) / RPM</div>
                  <p className="text-[11px] text-muted-foreground">HP = (Torque × RPM) / 5,252.</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Pump Affinity Laws</div>
                  <div className="font-mono text-amber-600 dark:text-amber-400 font-bold">Q ∝ N,  H ∝ N²,  P ∝ N³</div>
                  <p className="text-[11px] text-muted-foreground">Flow is linear; Head is squared; Power is cubed with speed.</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Thermal Shaft Growth</div>
                  <div className="font-mono text-amber-600 dark:text-amber-400 font-bold">ΔL = L × α × ΔT</div>
                  <p className="text-[11px] text-muted-foreground">Steel α ≈ 0.0000065 in/in/°F. Critical for shaft alignment.</p>
                </div>
              </CardContent>
            </Card>

            {/* USPS 955 & Rigging Formulas */}
            <Card className="border-purple-500/30">
              <CardHeader className="bg-purple-500/5 border-b border-border/60 pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-purple-600 dark:text-purple-400">
                  <Compass className="h-4 w-4" />
                  USPS 955 &amp; Rigging Standards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4 text-xs">
                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Bridle Sling Leg Tension</div>
                  <div className="font-mono text-purple-600 dark:text-purple-400 font-bold">Tension = (Load / n) / sin(θ)</div>
                  <p className="text-[11px] text-muted-foreground">At 30° horizontal, each leg carries 100% of the entire load weight!</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">Tap Drill Sizing (60° V-Threads)</div>
                  <div className="font-mono text-purple-600 dark:text-purple-400 font-bold">Tap Drill = Major Dia - (1 / TPI)</div>
                  <p className="text-[11px] text-muted-foreground">Example for 1/2"-13: 0.500 - (1/13) = 0.423" (27/64" drill).</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">SAE Steel Bolt Grade ID</div>
                  <div className="font-mono text-purple-600 dark:text-purple-400 font-bold">Grade = Radial Hash Marks + 2</div>
                  <p className="text-[11px] text-muted-foreground">3 slashes = Grade 5 (medium). 6 slashes = Grade 8 (alloy steel).</p>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/60 border border-border space-y-1">
                  <div className="font-bold text-foreground">De Morgan's Digital Logic (ET)</div>
                  <div className="font-mono text-purple-600 dark:text-purple-400 font-bold">!(A · B) = !A + !B</div>
                  <p className="text-[11px] text-muted-foreground">NAND gate is equivalent to negative-OR gate.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudyLabPage;
